<template>
  <span v-if="shown" ref="triggerEl" class="provInline" @mouseenter="onHover" @mouseleave="showCard = false">
    <span class="provArrow">&larr;</span>
    <svg v-if="provider === 'user'" class="provUserIcon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm5 6.5a.5.5 0 0 0 .5-.5 5.5 5.5 0 0 0-11 0 .5.5 0 0 0 .5.5h10Z"/></svg>
    <AppIcon v-else-if="iconName" :name="iconName" :size="14" />
    <FavIcon v-else-if="iconDomain" :domain="iconDomain" :size="14" />
    <AppIcon v-else name="integration" :size="14" />
    <template v-if="detailed">
      <span v-if="leadiqStatus" class="provBadge" :class="`liq-${leadiqStatus}`">{{ leadiqLabel }}</span>
      <span v-else-if="provider === 'leadiq'" class="provBadge liq-present">present</span>
    </template>

    <!-- Hover card: who set it and when -->
    <Transition name="hovercard">
      <div v-if="showCard && (hasDetails || hcLoading)" class="hoverCard" :class="{ flipped: flipUp }">
        <div v-if="hcLoading" class="hcRow"><span class="hcValue hcLoading">Loading...</span></div>
        <template v-else-if="hc">
          <div v-if="hc.actor_type" class="hcRow">
            <span class="hcIcon"><AppIcon name="contact" :size="12" :color="hc.actor_type === 'system' ? '#9ca3af' : '#3b82f6'" /></span>
            <span class="hcValue">{{ hc.actor_name || hc.actor_type }}</span>
          </div>
          <div v-if="hc.interaction_type" class="hcRow">
            <span class="hcIcon"><AppIcon name="email_setting" :size="12" color="#9ca3af" /></span>
            <span class="hcValue">{{ formatInteraction(hc.interaction_type) }}</span>
          </div>
          <div v-if="hc.crm_status && hc.crm_status !== 'not_checked'" class="hcRow">
            <span class="hcIcon"><AppIcon :name="CRM_ICONS[hc.crm_checked || ''] || 'integration'" :size="12" /></span>
            <span class="hcValue">{{ hc.crm_status === 'exists' ? `In ${CRM_LABELS[hc.crm_checked || ''] || hc.crm_checked || 'CRM'}` : `Not in ${CRM_LABELS[hc.crm_checked || ''] || hc.crm_checked || 'CRM'}` }}</span>
          </div>
          <div v-if="hc.created_at" class="hcRow">
            <span class="hcIcon"><AppIcon name="calendar" :size="12" color="#9ca3af" /></span>
            <span class="hcValue">{{ formatDate(hc.created_at) }}</span>
          </div>
        </template>
      </div>
    </Transition>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import FavIcon from './FavIcon.vue'
import { apiFetch } from '@/services/api'
import { showsProvenanceMarker } from './verificationMarks'

type HovercardData = {
  provider?: string | null
  leadiq_status?: string | null
  actor_type?: string | null
  actor_name?: string | null
  interaction_type?: string | null
  crm_status?: string | null
  crm_checked?: string | null
  created_at?: string | null
}

const props = defineProps<{
  provider: string | null | undefined
  detailed?: boolean
  leadiqStatus?: string | null
  dataType?: string | null
  dataValue?: string | null
}>()

const shown = computed(() => (props.detailed ? !!props.provider : showsProvenanceMarker(props.provider)))

const triggerEl = ref<HTMLElement | null>(null)
const showCard = ref(false)
const flipUp = ref(false)
const hcLoading = ref(false)
const hcFetched = ref<HovercardData | null>(null)
const hcMissed = ref(false)

const hc = computed<HovercardData | null>(() => hcFetched.value)
const hasDetails = computed(() => !!hcFetched.value)

watch(() => [props.dataType, props.dataValue], () => {
  hcFetched.value = null
  hcMissed.value = false
})

async function onHover() {
  // Check if there's enough space below (120px for the card).
  if (triggerEl.value) {
    const rect = triggerEl.value.getBoundingClientRect()
    flipUp.value = (window.innerHeight - rect.bottom) < 120
  }
  showCard.value = true
  if (hcFetched.value || hcMissed.value || hcLoading.value) return
  if (!props.dataType || !props.dataValue) return
  hcLoading.value = true
  try {
    const params = new URLSearchParams({
      data_type: props.dataType,
      data_value: props.dataValue,
    })
    const resp = await apiFetch(`/provenance/lookup?${params}`)
    if (resp) hcFetched.value = resp as HovercardData
    else hcMissed.value = true
  } catch {
    // Silently fail — hovercard just won't show.
    hcMissed.value = true
  } finally {
    hcLoading.value = false
  }
}

const APPICON_PROVIDERS: Record<string, string> = {
  salesforce: 'salesforce',
  hubspot: 'hubspot',
  pipedrive: 'pipedrive',
  leadiq: 'leadiq',
}

const FAVICON_PROVIDERS: Record<string, string> = {
  exa: 'exa.ai',
  openai: 'openai.com',
  claude: 'anthropic.com',
  warmly: 'warmly.ai',
  gong: 'gong.io',
}

const iconName = computed<string | null>(() =>
  props.provider ? APPICON_PROVIDERS[props.provider] || null : null
)

const iconDomain = computed<string | null>(() =>
  props.provider ? FAVICON_PROVIDERS[props.provider] || null : null
)

const LEADIQ_LABELS: Record<string, string> = {
  verified: 'verified',
  verified_likely: 'verified likely',
  unverified: 'unverified',
  invalid: 'invalid',
  suppressed: 'suppressed',
  person_not_found: 'not found',
  no_longer_current: 'past email',
  different_email: 'different email',
}

const leadiqLabel = computed(() => {
  if (!props.leadiqStatus) return ''
  // LeadIQ's own product calls a likely-accurate mobile exactly that, never "verified".
  if (props.dataType === 'phone' && props.leadiqStatus === 'verified_likely') return 'likely accurate'
  return LEADIQ_LABELS[props.leadiqStatus] || props.leadiqStatus
})

const CRM_ICONS: Record<string, string> = {
  salesforce: 'salesforce',
  hubspot: 'hubspot',
  pipedrive: 'pipedrive',
}

const CRM_LABELS: Record<string, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
}

const INTERACTION_LABELS: Record<string, string> = {
  pipeline_run: 'Pipeline run',
  chat_action: 'Chat',
  csv_upload: 'CSV upload',
  manual_edit: 'Manual edit',
  agent_run: 'Agent run',
  crm_sync: 'CRM sync',
  enrichment: 'LeadIQ enrichment',
  webhook: 'Webhook',
  source_run: 'Audience source run',
  backfill: 'Backfill',
  sender_resolution: 'Sender resolution',
  extension_capture: 'Captured from the LeadIQ extension',
  dial_list_push: 'Pushed from the LeadIQ extension',
  adhoc_list_push: 'Pushed from the LeadIQ extension',
}

function formatInteraction(raw: string): string {
  return INTERACTION_LABELS[raw] || raw.replace(/_/g, ' ')
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}
</script>

<style scoped>
.provInline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  vertical-align: middle;
  position: relative;
  cursor: default;
}

.provArrow {
  font-size: 12px;
  color: var(--muted, #9ca3af);
  line-height: 0;
}

.provUserIcon {
  color: var(--color-text-secondary, #6b7280);
}

.provBadge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;
}
.liq-verified { background: #d1fae5; color: #065f46; }
.liq-verified_likely { background: #d1fae5; color: #047857; }
.liq-unverified { background: #fef3c7; color: #92400e; }
.liq-invalid { background: #fee2e2; color: #991b1b; }
.liq-suppressed { background: #f1f5f9; color: #475569; }
.liq-present { background: var(--status-neutral-bg, #f1f5f9); color: var(--status-neutral-text, #475569); }
.liq-person_not_found { background: #f1f5f9; color: #6b7280; }
.liq-no_longer_current { background: #fef3c7; color: #92400e; }
.liq-different_email { background: #fef3c7; color: #92400e; }

/* ── Hover card ────────────────────────────────────────────────── */
.hoverCard {
  position: absolute;
  top: calc(100% + 6px);
  bottom: auto;
  left: 0;
  z-index: 100;
  min-width: 180px;
  padding: 8px 10px;
  margin-right: 12px;
  background: var(--color-bg-surface, #fff);
  border: 1px solid var(--color-border-subtle, #e5e2dc);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 5px;
  pointer-events: none;
}

.hoverCard.flipped {
  top: auto;
  bottom: calc(100% + 6px);
}

.hcRow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hcIcon {
  flex-shrink: 0;
  color: var(--color-text-tertiary, #9ca3af);
  display: flex;
  align-items: center;
}

.hcValue {
  font-size: 12px;
  color: var(--color-text-primary, #1f2937);
  font-weight: 500;
  white-space: nowrap;
}

.hcLoading {
  color: var(--color-text-tertiary, #9ca3af);
  font-style: italic;
}

/* ── Transition ────────────────────────────────────────────────── */
.hovercard-enter-active { transition: opacity 120ms ease, transform 120ms ease; }
.hovercard-leave-active { transition: opacity 80ms ease; }
.hovercard-enter-from { opacity: 0; transform: translateY(-4px); }
.hovercard-leave-to { opacity: 0; }
</style>
